'use client';

import type { TranslationKey } from '@freedraw/i18n';
import { useAppTranslation } from '@freedraw/i18n/react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  BriefcaseBusiness,
  CodeXml,
  GraduationCap,
  LayoutDashboard,
  Palette,
  Plus,
  type LucideIcon
} from 'lucide-react';
import { useState } from 'react';

import { DISPLAY_FONT } from '@/components/landing/landing-fonts';
import { BRAND } from '@/theme';

const INK = BRAND.ink;
const SUBTLE = '#596275';
const DASHED = '1px dashed #DEE3EE';

type Persona = {
  icon: LucideIcon;
  titleKey: TranslationKey;
  bodyKey: TranslationKey;
  tags: TranslationKey[];
};

const PERSONAS: Persona[] = [
  {
    icon: LayoutDashboard,
    titleKey: 'landing.personaProductTitle',
    bodyKey: 'landing.personaProductBody',
    tags: ['landing.typeGantt', 'landing.typeTimeline', 'landing.typeKanban']
  },
  {
    icon: CodeXml,
    titleKey: 'landing.personaEngineerTitle',
    bodyKey: 'landing.personaEngineerBody',
    tags: ['landing.typeSequence', 'landing.typeEr', 'landing.typeFlowchart']
  },
  {
    icon: Palette,
    titleKey: 'landing.personaDesignerTitle',
    bodyKey: 'landing.personaDesignerBody',
    tags: ['landing.typeFlowchart', 'landing.typeMindmap']
  },
  {
    icon: GraduationCap,
    titleKey: 'landing.personaTeacherTitle',
    bodyKey: 'landing.personaTeacherBody',
    tags: ['landing.typeMindmap', 'landing.typeTimeline']
  },
  {
    icon: BriefcaseBusiness,
    titleKey: 'landing.personaManagerTitle',
    bodyKey: 'landing.personaManagerBody',
    tags: ['landing.typeOrg', 'landing.typeFlowchart', 'landing.typeKanban']
  }
];

const FAQ: Array<[TranslationKey, TranslationKey]> = [
  ['landing.faqWhatQ', 'landing.faqWhatA'],
  ['landing.faqCodeQ', 'landing.faqCodeA'],
  ['landing.faqEditQ', 'landing.faqEditA'],
  ['landing.faqTeamQ', 'landing.faqTeamA'],
  ['landing.faqExportQ', 'landing.faqExportA'],
  ['landing.faqStartQ', 'landing.faqStartA']
];

const eyebrowSx = {
  fontSize: 14,
  fontWeight: 700,
  color: BRAND.pinkStrong,
  letterSpacing: '0.06em',
  textTransform: 'uppercase'
} as const;

export function LandingPersonasSection() {
  const { t } = useAppTranslation();
  return (
    <Container component="section" maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
      <Box data-reveal sx={{ maxWidth: 760 }}>
        <Typography sx={eyebrowSx}>{t('landing.personasEyebrow')}</Typography>
        <Typography
          component="h2"
          sx={{ mt: 1.5, fontFamily: DISPLAY_FONT, textWrap: 'balance', fontWeight: 600, fontSize: { xs: 34, md: 52 }, lineHeight: 1.06, letterSpacing: '-0.02em' }}
        >
          {t('landing.personasTitle')}
        </Typography>
      </Box>
      <Box component="ul" data-reveal sx={{ listStyle: 'none', m: 0, mt: { xs: 5, md: 7 }, p: 0, borderTop: DASHED }}>
        {PERSONAS.map(({ icon: Icon, titleKey, bodyKey, tags }) => (
          <Box
            component="li"
            key={titleKey}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 1.2fr) minmax(0, 1fr)' },
              alignItems: 'center',
              gap: { xs: 1.5, md: 4 },
              py: { xs: 2.5, md: 2.75 },
              borderBottom: DASHED
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  borderRadius: '12px',
                  bgcolor: BRAND.pinkLight,
                  color: BRAND.pinkStrong,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Icon size={21} strokeWidth={1.9} />
              </Box>
              <Typography component="h3" sx={{ fontFamily: DISPLAY_FONT, textWrap: 'balance', fontWeight: 600, fontSize: { xs: 24, md: 30 }, lineHeight: 1.15 }}>
                {t(titleKey)}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 16, lineHeight: 1.55, color: SUBTLE }}>{t(bodyKey)}</Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} justifyContent={{ md: 'flex-end' }}>
              {tags.map((tag) => (
                <Box
                  key={tag}
                  component="span"
                  sx={{
                    px: 1.25,
                    py: 0.5,
                    borderRadius: '999px',
                    border: '1px solid #e4dde1',
                    bgcolor: '#ffffff',
                    fontSize: 13,
                    fontWeight: 600,
                    color: INK,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {t(tag)}
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Box>
    </Container>
  );
}

export function LandingFaqSection() {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState<number | null>(0);
  return (
    <Box component="section" id="faq" sx={{ scrollMarginTop: 64, bgcolor: '#ffffff', borderTop: '1px solid #DEE3EE' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 4, md: 8 }}>
          <Box data-reveal sx={{ flex: '0 0 auto', width: { md: 320 } }}>
            <Typography
              component="h2"
              sx={{ fontFamily: DISPLAY_FONT, textWrap: 'balance', fontWeight: 600, fontSize: { xs: 36, md: 48 }, lineHeight: 1.06, letterSpacing: '-0.02em' }}
            >
              {t('landing.faqTitle')}
            </Typography>
            <Typography sx={{ mt: 2, fontSize: 16, lineHeight: 1.55, color: SUBTLE }}>{t('landing.faqBody')}</Typography>
          </Box>
          <Box data-reveal sx={{ flex: 1, minWidth: 0, borderTop: DASHED }}>
            {FAQ.map(([question, answer], index) => {
              const expanded = open === index;
              return (
                <Accordion
                  key={question}
                  expanded={expanded}
                  onChange={(_, isOpen) => setOpen(isOpen ? index : null)}
                  disableGutters
                  elevation={0}
                  square
                  sx={{ bgcolor: 'transparent', border: 'none', borderBottom: DASHED, '&::before': { display: 'none' } }}
                >
                  <AccordionSummary
                    data-testid={`landing-faq-${index}`}
                    expandIcon={<Plus size={20} strokeWidth={2} color={INK} />}
                    sx={{
                      px: 0,
                      minHeight: 72,
                      '& .MuiAccordionSummary-content': { my: 2 },
                      '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(45deg)' }
                    }}
                  >
                    <Typography component="h3" sx={{ fontSize: { xs: 16.5, md: 18 }, fontWeight: 700, pr: 2 }}>
                      {t(question)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0, pb: 3 }}>
                    <Typography sx={{ fontSize: 16, lineHeight: 1.65, color: SUBTLE, maxWidth: 680 }}>{t(answer)}</Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
